import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES, PROJECT_FORM_MODE } from "../ProjectForm/projectFormStore";
import {
    PROJECT_FORM_CONTROLLER_NAMESPACE,
    PROJECT_FORM_CONTROLLER_TYPES
} from "../ProjectForm/projectFormControllerStore";
import { PROJECT_NAMESPACE, PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { isModuleRegistered } from "SharedStores/util";
import validationRenderHelper from "Validation/_validation-error-helper.vue";
import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
export { getValidator, VALIDATOR_TYPES, validationRenderHelper };

import { THOUSAND_SEPARATOR_FORMATTER, INTEGER_FORMATTER, getDataFormatter } from "Util/dataFormatters";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes.js";
import { getResponsiveClassMap } from "Responsive/manager";
import { useProjectLockStore } from "Projects/projectLockStore.js";
import { TEXT_VALIDATION_RULE_TYPES } from "ProgramAdmin/DataFields/TextValidationRules/textValidationRulesTab.vue";
import { getTextValidationRuleValidatorOptions } from "ProgramAdmin/DataFields/TextValidationRules/textValidationRuleValidationHelpers";
import { mapI18n } from "Util/i18n";
import {
    KEYS as TVR_KEYS,
    NAMESPACE as TVR_NAMESPACE
} from "ProgramAdmin/DataFields/TextValidationRules/i18n/textValidationRulesTab.i18n";

const Name = {
    First: "First",
    Last: "Last"
};

const Address = {
    Line1: "Line 1",
    Line2: "Line 2",
    State: "State",
    City: "City",
    ZipCode: "Zip Code",
    County: "County"
};

const Contact = {
    Company: "Company",
    Phone: "Phone",
    Email: "Email",
    ...Address,
    ...Name
};

// Sensitive data is a unique data field group in that it's
// a group pretending to be a single field. The obfuscated
// value is the only one ever directly revealed to the client
// so its suffix is the empty string
const SensitiveData = {
    Obfuscated: "",
    Encrypted: "Encrypted"
};

// The suffixes for a custom list element depend on which
// custom list data field group it is configured to represent,
// but these suffixes are standard for all custom list elements
const CustomList = {
    RowId: "RowId",
    Count: "Count"
};

export const SUFFIXES = {
    Name,
    Address,
    Contact,
    SensitiveData,
    CustomList
};

export function mapGroupIdsAndValues(fields) {
    const map = {};
    for (const name in fields) {
        if (fields.hasOwnProperty(name)) {
            const suffix = fields[name];
            const id = `${name}Id`;
            map[id] = function () {
                return this.element.GroupDataFields[suffix];
            };
            map[name] = function () {
                return this.getProjectValue(this[id]);
            };
        }
    }
    return map;
}

export const getOptionsFromAuxiliaryModelMixin = {
    methods: {
        getOptionsFromAuxiliaryModel() {
            if (this.element.AuxiliaryModel.ListView) {
                //prettier-ignore
                const listView = this.element.AuxiliaryModel.ListView
                    .filter(item => item.Name)
                    .sort((left, right) => left.Order - right.Order);

                if (this.value) {
                    const listItem = this.$store.getters[PROGRAM_TYPES.getters.listItemFromId](this.value);
                    //check that the listView has the current item. if not add it to the list
                    if (listView.every((item) => item.Name !== listItem.Value)) {
                        listView.push({
                            Label: listItem.ItemValue,
                            ListItemDefinitionId: listItem.ListItemDefinitionId,
                            Name: listItem.ItemValue,
                            Order: Number.MAX_VALUE
                        });
                    }
                }

                return listView;
            }
            return [];
        }
    }
};

const responsiveHelpers = {
    short: {
        col: { xs: 6, sm: 4, md: 3, lg: 2 }
    },
    standard: {
        col: { xs: 12, sm: 6, lg: 4 }
    },
    long: {
        col: { xs: 12, sm: 10, lg: 6 }
    }
};

export default {
    props: {
        element: { type: Object, required: true },
        isInFormEditor: { type: Boolean, required: false }
    },
    inject: {
        viewport: {
            from: "viewport",
            default: null
        }
    },
    data() {
        const projectLockStore = useProjectLockStore();
        return {
            validationResult: null,
            projectLockStore: projectLockStore
        };
    },
    computed: {
        label() {
            return this.element.Properties.label;
        },
        required() {
            return this.getBooleanPropertyValue("required");
        },
        isAdmin() {
            return (
                this.projectFormMode === PROJECT_FORM_MODE.Admin ||
                this.projectFormMode === PROJECT_FORM_MODE.AdminReadonly
            );
        },
        isView() {
            const mode = this.projectFormMode;
            return (
                mode === PROJECT_FORM_MODE.View ||
                mode === PROJECT_FORM_MODE.Print ||
                mode === PROJECT_FORM_MODE.AdminReadonly
            );
        },
        isPrefill() {
            const mode = this.projectFormMode;
            return mode === PROJECT_FORM_MODE.Prefill || mode === PROJECT_FORM_MODE.PrefillAdmin;
        },
        projectFormMode() {
            if (this.isProjectFormModuleRegistered()) {
                return this.$store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode;
            }
            return PROJECT_FORM_MODE.Edit;
        },
        isPrinterFriendly() {
            if (this.isProjectFormModuleRegistered()) {
                return this.$store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode === PROJECT_FORM_MODE.Print;
            }
            return false;
        },
        baseReadonly() {
            return (
                this.getBooleanPropertyValue("readonly") ||
                this.projectLockStore.isLocked ||
                this.isView ||
                this.isImmutableSingleFieldElement
            );
        },
        // If this is a non-group element whose data field is marked immutable,
        // the form element will be treated as readonly. This will need to be modified
        // if at some point we allow group elements with individual immutable fields.
        isImmutableSingleFieldElement() {
            if (!this.dataField) {
                return false;
            }
            return this.dataField.IsImmutable;
        },
        // Individual elements may override readonly under specific conditions like prefill mode
        readonly() {
            return this.baseReadonly;
        },
        dataFieldId() {
            return this.element.DataFieldId;
        },
        dataField() {
            return this.dataFieldId
                ? this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](this.dataFieldId)
                : null;
        },
        value() {
            return this.getProjectValue(this.dataFieldId);
        },
        projectElement() {
            if (this.isProjectFormModuleRegistered()) {
                return this.$store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](this.element.PublicId);
            }
            return null;
        },
        validator() {
            const additionalValidatorOptions =
                (Object.hasOwn(this, "getAdditionalValidatorOptions") && this.getAdditionalValidatorOptions()) || [];
            let options = [];
            if (this.required && !this.isInFormEditor) {
                options.push({ type: VALIDATOR_TYPES.required });
            }

            const textValidationRuleValidator = this.getTextValidationRuleValidatorIfAny();
            if (textValidationRuleValidator) {
                options.push(textValidationRuleValidator);
            }
            options = [...options, ...additionalValidatorOptions];
            if (options.length > 0) {
                return getValidator(this.$app, options);
            }
            return null;
        },
        hasTextValidationRule() {
            return this.dataField && this.dataField.TextValidationRuleId;
        },
        validationError() {
            if (this.validationResult) {
                return this.validationResult.error;
            }
            return null;
        },
        dataFormatter() {
            if (!this.dataField) {
                return null;
            }
            const formatters = [];
            if (this.dataField.TypeId === DATA_FIELD_TYPE.Integer) {
                formatters.push(INTEGER_FORMATTER);
            }
            if (this.dataField.ShowSeparators) {
                formatters.push(THOUSAND_SEPARATOR_FORMATTER);
            }
            if (formatters.length > 0) {
                return getDataFormatter(formatters);
            }
            return null;
        },
        showHelp() {
            return Boolean(this.element.Properties.helptext || this.element.Properties.helplink);
        },
        showHelpLinkOnly() {
            return this.showHelp && !this.element.Properties.helptext ? this.element.Properties.helplink : null;
        },
        binder() {
            let val = this.value;
            if (Object.hasOwn(this, "getValueOverride")) {
                val = this.getValueOverride(this.value);
            }

            return {
                modelValue: val,
                id: this.inputId,
                required: this.required,
                validator: this.validator,
                showHelp: this.showHelp,
                helpLink: this.showHelpLinkOnly,
                showValidationErrors: false,
                dataFormatter: this.dataFormatter,
                readonly: this.readonly
            };
        },
        /**
         * Id of the input to focus when the label is clicked
         */
        inputId() {
            return this.element.PublicId + "Input";
        },
        listeners() {
            return {
                "update:modelValue": this.onInputCommon,
                validated: this.onValidatedCommon
            };
        },
        responsiveHelpers() {
            return getResponsiveClassMap(this.viewport, responsiveHelpers);
        },
        textValidationRule() {
            if (!this.dataField || !this.dataField.TextValidationRuleId) {
                return null;
            }

            return this.$store.getters[PROGRAM_TYPES.getters.getTextValidationRuleById](
                this.dataField.TextValidationRuleId
            );
        },
        ...mapI18n(TVR_KEYS, TVR_NAMESPACE)
    },
    methods: {
        emitHelpClicked() {
            // Don't open help texts in printer-friendly mode, since they can appear
            // in the printout if we do so
            if (this.isPrinterFriendly) {
                return;
            }

            this.$emit("help-clicked");
        },

        /**
         * Shared handler for simple elements to relay data changes to store
         */
        onInputCommon(value) {
            if (value != null) {
                value = value.toString();
            }
            this.sendDataChange(this.dataFieldId, value);
        },
        /**
         * Shared handler for simple elements to listen for validation
         */
        onValidatedCommon(validationResult) {
            this.validationResult = validationResult;
        },
        async saveFormData() {
            if (this.isProjectFormControllerModuleRegistered()) {
                await this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, null, { root: true });
            }
        },
        isProjectModuleRegistered() {
            return isModuleRegistered(PROJECT_NAMESPACE, this.$store.state);
        },
        isProjectFormModuleRegistered() {
            return isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state);
        },
        isProjectFormControllerModuleRegistered() {
            return isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state);
        },
        getBooleanPropertyValue(propName) {
            const propVal = this.element.Properties[propName] || "";
            return propVal.toLowerCase() === "true";
        },
        getNumericPropertyValue(propName) {
            const propVal = this.element.Properties[propName] || null;
            if (propVal === null) {
                return null;
            }
            const numVal = Number(propVal);
            //check for NaN, infinity, -infinity
            if (!Number.isFinite(numVal)) {
                return null;
            }
            return numVal;
        },
        getProjectValue(dataFieldId) {
            if (this.isProjectModuleRegistered()) {
                return this.$store.getters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
            }
            return null;
        },
        getProjectMultiValue(dataFieldId) {
            if (this.isProjectModuleRegistered()) {
                return this.$store.getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](dataFieldId);
            }
            return null;
        },
        sendDataChange(dataFieldId, value) {
            if (this.isProjectFormModuleRegistered()) {
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setProjectData,
                    {
                        dataFieldId,
                        value
                    },
                    { root: true }
                );
            }
        },
        getTextValidationRuleValidatorIfAny() {
            if (!this.hasTextValidationRule || this.textValidationRule.RuleSchema === null) {
                return null;
            }

            // can't pass in this.dataField.TextValidationRule.RuleSchema directly b/c the validators expect lowercase js-style properties
            const ruleSchema = {
                allowedCharacterSets: this.textValidationRule.RuleSchema.CharacterSets,
                maxLength: this.textValidationRule.RuleSchema.MaxLength,
                allowedPatterns: this.textValidationRule.RuleSchema.AllowedPatterns
            };

            return getTextValidationRuleValidatorOptions(
                this.textValidationRule.TextValidationRuleTypeId,
                ruleSchema,
                this.getTextValidationRuleErrorMessage()
            );
        },
        getTextValidationRuleErrorMessage() {
            if (this.hasTextValidationRule) {
                const tvr = this.textValidationRule;

                if (tvr.ValidationErrorMessage !== null && tvr.ValidationErrorMessage !== "") {
                    return tvr.ValidationErrorMessage;
                } else if (tvr.TextValidationRuleTypeId === TEXT_VALIDATION_RULE_TYPES.CHAR_SET) {
                    return this.defaultCharSetValidationError;
                } else if (tvr.TextValidationRuleTypeId === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH) {
                    return this.defaultPatternMatchValidationError;
                }
            }
            return "";
        }
    }
};
